import React, { useMemo, useState } from 'react';
import { Box, FormControl, FormLabel, Stack, CircularProgress, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Fuse from 'fuse.js';
import { useDispatch, useSelector } from 'react-redux';
import {
	useGetBrokerGroupsQuery,
	useGetBrokerGroupUsersQuery,
	useGetUserDetailsQuery,
} from '@/features/user/userApi';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import SearchField from '@/components/SearchField';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { columns as dataColumns } from './data';
import {
	BROKERAGE_GROUPS_FUSE_OPTIONS,
	DEFAULT_HIDDEN_BROKERAGE_COLUMNS,
} from './helpers';
import { openDialog } from '@/features/dialog/dialogSlice';

export const BrokerageGroupsTable = () => (
	<ColumnControlContextProvider
		variant='brokerageGroups'
		value='list'
		defaultHiddenColumns={DEFAULT_HIDDEN_BROKERAGE_COLUMNS}
	>
		<BrokerageGroupsTableContent />
	</ColumnControlContextProvider>
);

const BrokerageGroupsTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [searchValue, setSearchValue] = useState('');

	const { data: userDetails, isLoading: isLoadingUser } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email },
	);

	const {
		data: brokerGroups = [],
		isLoading: isLoadingGroups,
		isError,
	} = useGetBrokerGroupsQuery(
		{ ids: userDetails?.brokerGroupArray, showAdmin: false },
		{ skip: !userDetails?.brokerGroupArray?.length },
	);

	const { data: brokerGroupUsers = [], isLoading: isLoadingUsers } = useGetBrokerGroupUsersQuery(
		{
			userId: userDetails?.userId,
			showGroup: true,
		},
		{ skip: !userDetails?.userId || !userDetails?.canViewGroup },
	);

	const rows = useMemo(
		() =>
			(brokerGroups ?? [])
				.filter(({ id }) => id)
				.map((group) => ({
					id: group.id,
					name: group.name ?? '-',
					contactEmail: group.contactEmail || '-',
					claimsEmail: group.claimsEmail ?? '',
					phoneNumber: group.phoneNumber?.toString() ?? '',
					website: group.website ?? '',
					fspNumber: group.fspNumber?.toString() ?? '',
					address: group.address ?? '',
					country: group.country ?? '',
					ledger: group.ledger ?? '',
					users: brokerGroupUsers.filter((user) =>
						user.brokerGroupArray?.includes(group.id),
					),
					lastUpdated: group.lastUpdated ?? null,
					lastUpdatedBy: group.lastUpdatedBy ?? null,
				})),
		[brokerGroups, brokerGroupUsers],
	);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(BROKERAGE_GROUPS_FUSE_OPTIONS.keys, rows);
		return new Fuse(rows, BROKERAGE_GROUPS_FUSE_OPTIONS, fuseIndex);
	}, [rows]);

	const filteredRows = useMemo(() => {
		if (!searchValue.trim()) return rows;
		return searchFuse.search(searchValue).map((result) => result.item);
	}, [rows, searchFuse, searchValue]);

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(dataColumns);

	const isLoading = isLoadingUser || isLoadingGroups || isLoadingUsers;

	if (isLoadingUser && !userDetails) {
		return <CircularProgress />;
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', sm: 'flex-end' }}
				sx={{ mb: 2 }}
			>
				<FormControl sx={{ width: { xs: '100%', sm: 320 } }}>
					<FormLabel sx={{ mb: 0.5, lineHeight: '1.5', display: 'block' }}>
						Search
					</FormLabel>
					<SearchField
						value={searchValue}
						onChange={(event) => setSearchValue(event.target.value)}
						onReset={() => setSearchValue('')}
						helperText=''
					/>
				</FormControl>
			</Stack>

			<Box
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: '8px',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<DataGrid
					initialState={{
						sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
					}}
					loading={isLoading}
					rows={filteredRows}
					columns={orderedColumns}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={onColumnVisibilityModelChange}
					onColumnWidthChange={onColumnWidthChange}
					disableRowSelectionOnClick
					hideFooterPagination
					hideFooter
					disableColumnFilter
					onRowClick={({ row }) => {
						if (!row?.id) return;
						dispatch(
							openDialog({
								dialogName: 'brokerageProfile',
								data: { docId: row.id },
							}),
						);
					}}
					sx={{
						border: 0,
						borderRadius: '8px',
						'& .MuiDataGrid-cell': { outline: 'none' },
						'& .MuiDataGrid-row': { cursor: 'pointer' },
						'--DataGrid-overlayHeight': '3em',
					}}
					slots={{
						noRowsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='broker groups' />
							: () => <NoRowsOverlay objectType='broker groups' />,
						noResultsOverlay: isError
							? () => <NoRowsErrorOverlay objectType='broker groups' />
							: () => <NoRowsOverlay objectType='broker groups' />,
						toolbar: DataGridColumnReorderToolbar,
						columnMenu: DataGridColumnMenu,
					}}
					slotProps={{
						loadingOverlay: {
							variant: 'skeleton',
							noRowsVariant: 'skeleton',
						},
					}}
				/>
			</Box>
		</Box>
	);
};

export default BrokerageGroupsTable;
